import { useNavigate } from "@tanstack/react-router";
import { Copy, Eye } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { LibraryTemplate } from "@/common/types/templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TemplateForm } from "@/components/ui/templates/template-form";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { useTemplateWrites } from "@/hooks/templates/useTemplateWrites";
import { toast } from "@/hooks/use-toast";
import { refreshTemplateReads } from "@/lib/template-queries";
import { getVisitScope } from "@/lib/visit-queries";
import type { TemplateFormSchemaType } from "@/schemas/template.schema";

// View and "Copy to My Templates" for one library template: the dialogs and
// their actions, shared by the classic row and the redesigned one.
export const useLibraryItemActions = (
  template: LibraryTemplate,
): {
  view: () => void;
  copy: () => void;
  loading: boolean;
  dialogs: JSX.Element;
} => {
  const { t } = useTranslation();
  const [isViewing, setIsViewing] = useState(false);
  const [isCopyingDialogOpen, setIsCopyingDialogOpen] = useState(false);
  const { create, loading } = useTemplateWrites();
  const copyAttempt = useRef(0);
  useEffect(
    () => () => {
      copyAttempt.current++;
    },
    [],
  );
  const changeCopyDialog = (open: boolean): void => {
    if (!open) copyAttempt.current++;
    setIsCopyingDialogOpen(open);
  };
  const navigate = useNavigate();

  // Convert LibraryTemplate to Template-like object for the form
  // Create a fresh copy of headers array to ensure form updates properly
  const templateForForm = {
    public_id: "",
    name: template.name,
    createDate: "",
    headers: [...(template.headers || [])],
    optionalText: template.optionalText || "",
    isDefault: 0,
  };

  const handleCopyTemplate = async (
    data: TemplateFormSchemaType,
  ): Promise<void> => {
    const attempt = copyAttempt.current;
    const outcome = await create({
      name: data.name,
      headers: data.headers,
      optionalText: data.optionalText ?? "",
      isDefault: data.isDefault,
    });
    if (!outcome || getVisitScope() !== outcome.scope) return;
    toast({
      variant: outcome.ok ? "default" : "error",
      title: t(
        outcome.ok ? "templates.copySuccess" : "templates.copyError.title",
      ),
      description: outcome.ok
        ? t("templates.copySuccessDescription", { name: data.name })
        : t("templates.copyError.description"),
    });
    if (outcome.ok) {
      const active = attempt === copyAttempt.current;
      if (active) changeCopyDialog(false);
      void refreshTemplateReads(outcome.scope);
      if (active) void navigate({ to: `/template/${outcome.value.public_id}` });
    }
  };

  const handleViewTemplate = (): void => {
    setIsViewing(true);
  };

  return {
    view: handleViewTemplate,
    copy: () => {
      setIsCopyingDialogOpen(true);
    },
    loading,
    dialogs: (
      <>
        {/* Copy Template Dialog with Edit Form */}
        <Dialog open={isCopyingDialogOpen} onOpenChange={changeCopyDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {t("templates.copyTemplateDialog.title")}
              </DialogTitle>
            </DialogHeader>
            <TemplateForm
              isSubmitting={loading}
              key={`${template.public_id}-${isCopyingDialogOpen}`}
              template={templateForForm}
              submitText={
                loading
                  ? t("templates.copyTemplateDialog.copyingButton")
                  : t("templates.copyTemplateDialog.copyButton")
              }
              onSubmit={handleCopyTemplate}
              onCancel={() => {
                changeCopyDialog(false);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Template Details Modal */}
        <AlertDialog open={isViewing} onOpenChange={setIsViewing}>
          <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-xl font-semibold">
                {template.name}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <div className="space-y-6 mt-4">
              {/* Template Sections */}
              <div>
                <h4 className="font-semibold text-base mb-3 text-grey-800">
                  {t("templates.templateDetails.sections")}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {template.headers.map((header) => (
                    <span
                      key={header}
                      className="inline-flex items-center bg-primary/10 text-primary px-3 py-1 rounded-md text-sm font-medium"
                    >
                      {header}
                    </span>
                  ))}
                </div>
              </div>

              {/* Full Instructions */}
              {template.optionalText && (
                <div>
                  <h4 className="font-semibold text-base mb-3 text-grey-800">
                    {t("templates.templateDetails.instructions")}
                  </h4>
                  <div className="bg-grey-50 rounded-lg p-4 border border-grey-200">
                    <div className="prose prose-sm max-w-none">
                      <FileResultRendererMD result={template.optionalText} />
                    </div>
                  </div>
                </div>
              )}

              {!template.optionalText && (
                <div className="text-sm text-grey-500 italic">
                  {t("templates.templateDetails.details")}
                </div>
              )}
            </div>
            <AlertDialogFooter className="mt-6">
              <AlertDialogAction
                onClick={() => {
                  setIsViewing(false);
                }}
              >
                {t("templates.templateDetails.closeButton")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    ),
  };
};

export const LibraryItem = ({
  template,
}: {
  template: LibraryTemplate;
}): JSX.Element => {
  const { t } = useTranslation();
  const { view, copy, loading, dialogs } = useLibraryItemActions(template);
  return (
    <>
      <div className="flex items-center justify-between py-4 px-6">
        <div className="space-y-1">
          <h3 className="text-primary font-medium text-base">
            {template.name}
          </h3>
          {template.optionalText && (
            <p className="text-sm text-grey-400 line-clamp-2">
              {template.optionalText.length > 150
                ? `${template.optionalText.substring(0, 150)}...`
                : template.optionalText}
            </p>
          )}
          <div className="text-xs text-grey-500">
            {t("templates.library.sectionCount", {
              count: template.headers.length,
            })}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            className="flex items-center space-x-1"
            size="sm"
            variant="outline"
            onClick={view}
          >
            <Eye className="w-4 h-4" />
            <span>{t("templates.viewTemplate")}</span>
          </Button>

          <Button
            className="flex items-center space-x-1"
            disabled={loading}
            size="sm"
            variant="default"
            onClick={copy}
          >
            <Copy className="w-4 h-4" />
            <span>
              {loading
                ? t("templates.copyTemplateDialog.copyingButton")
                : t("templates.copyToMyTemplates")}
            </span>
          </Button>
        </div>
      </div>

      {dialogs}
    </>
  );
};
